import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Trash2, Loader2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMessages = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('en');
  const [newMessage, setNewMessage] = useState({ en: '', ru: '', kz: '' });

  useEffect(() => {
    const isAuth = localStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    fetchMessages();
  }, [navigate]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/messages`, {
        message_text: newMessage,
        category: 'general'
      });
      setShowModal(false);
      setNewMessage({ en: '', ru: '', kz: '' });
      setActiveTab('en');
      fetchMessages();
    } catch (error) {
      console.error('Error creating message:', error);
      alert('Failed to create message');
    }
  };

  const handleDelete = async (messageId) => {
    if (!confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await axios.delete(`${API}/admin/messages/${messageId}`);
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message');
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-black">Pre-written Messages</h1>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-[#1E88E5] text-white rounded-lg hover:bg-[#1565C0] transition-colors flex items-center space-x-2"
            data-testid="create-message-btn"
          >
            <Plus className="w-5 h-5" />
            <span>Add Message</span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <p className="text-gray-600 mb-6">
            These messages can be selected by customers when customizing their gift cards.
          </p>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-12 h-12 text-[#1E88E5] animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-gray-600 text-center py-12">No pre-written messages yet</p>
          ) : (
            <div className="space-y-3">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="text-gray-800 italic">
                      "{typeof message.message_text === 'string' ? message.message_text : message.message_text?.en || ''}"
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Added {new Date(message.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(message.id)}
                    className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`delete-message-${message.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-black">Add New Message</h2>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setNewMessage({ en: '', ru: '', kz: '' });
                      setActiveTab('en');
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Language Tabs */}
                <div className="flex space-x-2 mb-6 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => setActiveTab('en')}
                    className={`px-4 py-2 font-semibold transition-colors border-b-2 ${
                      activeTab === 'en' 
                        ? 'text-[#1E88E5] border-[#1E88E5]' 
                        : 'text-gray-500 border-transparent hover:text-gray-700'
                    }`}
                  >
                    English
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('ru')}
                    className={`px-4 py-2 font-semibold transition-colors border-b-2 ${
                      activeTab === 'ru' 
                        ? 'text-[#1E88E5] border-[#1E88E5]' 
                        : 'text-gray-500 border-transparent hover:text-gray-700'
                    }`}
                  >
                    Русский
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('kz')}
                    className={`px-4 py-2 font-semibold transition-colors border-b-2 ${
                      activeTab === 'kz' 
                        ? 'text-[#1E88E5] border-[#1E88E5]' 
                        : 'text-gray-500 border-transparent hover:text-gray-700'
                    }`}
                  >
                    Қазақша
                  </button>
                </div>

                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      Message Text * ({activeTab.toUpperCase()})
                    </label>
                    <textarea
                      value={newMessage[activeTab] || ''}
                      onChange={(e) => setNewMessage({ 
                        ...newMessage, 
                        [activeTab]: e.target.value 
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                      placeholder={
                        activeTab === 'en' 
                          ? "Write a message that customers can use..." 
                          : activeTab === 'ru' 
                            ? "Напишите сообщение для клиентов..."
                            : "Тұтынушылар пайдалана алатын хабарлама жазыңыз..."
                      }
                      rows={4}
                      required={activeTab === 'en'}
                      data-testid="new-message-input"
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-[#1E88E5] text-white rounded-lg hover:bg-[#1565C0] transition-colors"
                      data-testid="save-message-btn"
                    >
                      Add Message
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;